import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

interface Option {
  id: string;
  label: string;
}

interface SearchableDropdownProps {
  label?: string;
  placeholder?: string;
  searchText: string;
  setSearchText: (text: string) => void;
  suggestionsVisible: boolean;
  setSuggestionsVisible: (visible: boolean) => void;
  options: Option[];
  onSelect: (option: Option) => void;
  dropdownRef?: React.RefObject<HTMLDivElement>;
  isLoading?: boolean;
  disableClientFilter?: boolean;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  placeholder,
  searchText,
  setSearchText,
  suggestionsVisible,
  setSuggestionsVisible,
  options,
  onSelect,
  dropdownRef,
  isLoading = false,
  disableClientFilter = false,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const filteredOptions = disableClientFilter
    ? options
    : options.filter((option) =>
        option.label.toLowerCase().includes(searchText.toLowerCase())
      );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setHighlightedIndex(0); // reset on searchText change
  }, [searchText]);

  return (
    <div className="relative">
      {label && <label className="block text-gray-700 text-sm font-medium mb-2">{label}</label>}
      <div className="relative flex items-center mb-1">
        <input
          type="text"
          ref={inputRef}
          value={searchText}
          placeholder={placeholder || `Search ${label}`}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value);
            setSuggestionsVisible(value.trim().length > 0);
          }}
          onFocus={() => {
            if (searchText.trim().length > 0) {
              setSuggestionsVisible(true);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const selected = filteredOptions[highlightedIndex];
              if (selected) {
                onSelect(selected);
                setSuggestionsVisible(false);
              }
            }
          }}
          className={`w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 ${
            isLoading ? "pr-9" : ""
          }`}
        />
        {isLoading && (
          <div className="absolute right-2.5 text-green-600 animate-spin flex items-center justify-center pointer-events-none">
            <Loader2 size={18} />
          </div>
        )}
      </div>

      {suggestionsVisible && searchText.trim().length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-50 mt-1"
          style={{ top: "100%" }}
        >
          {isLoading ? (
            <div className="p-4 text-gray-600 text-center flex items-center justify-center gap-2 font-medium text-sm">
              <Loader2 size={16} className="animate-spin text-green-600" />
              <span>Searching for product...</span>
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(option);
                  setSuggestionsVisible(false);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  highlightedIndex === index ? "bg-gray-200" : "hover:bg-gray-100"
                }`}
              >
                <div className="font-medium text-gray-900">{option.label}</div>
              </div>
            ))
          ) : (
            <div className="p-3 text-gray-500 text-center text-sm">
              No results found for "{searchText}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};
